// Комментарий на русском: постоянный кэш фотографий чатов через IndexedDB.
// Зачем: фото должны храниться между сессиями, очищаться только при выходе из чата или через настройки.

const DB_NAME = 'lisad_chat_photos_v2';
const DB_VERSION = 1;
const STORE_PHOTOS = 'photos';

interface PhotoCacheEntry {
  url: string;           // ключ — URL фото
  blob: Blob;            // бинарные данные
  mime: string;          // MIME-тип
  chatId: string;        // ID чата (dm:123 или room:456)
  ts: number;            // timestamp сохранения
}

let _db: IDBDatabase | null = null;

function canUseIDB(): boolean {
  return typeof window !== 'undefined' && typeof indexedDB !== 'undefined';
}

async function openDb(): Promise<IDBDatabase | null> {
  if (_db) return _db;
  if (!canUseIDB()) return null;
  
  return new Promise((resolve) => {
    try {
      const req = indexedDB.open(DB_NAME, DB_VERSION);
      req.onupgradeneeded = () => {
        const db = req.result;
        if (!db.objectStoreNames.contains(STORE_PHOTOS)) {
          const store = db.createObjectStore(STORE_PHOTOS, { keyPath: 'url' });
          store.createIndex('chatId', 'chatId', { unique: false });
          store.createIndex('ts', 'ts', { unique: false });
        }
      };
      req.onsuccess = () => {
        _db = req.result;
        resolve(_db);
      };
      req.onerror = () => resolve(null);
    } catch {
      resolve(null);
    }
  });
}

function asPromise<T>(req: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

function isSkippableSrc(src: string): boolean {
  return !src || src.startsWith('blob:') || src.startsWith('data:');
}

/**
 * Получить фото из постоянного кэша.
 * Возвращает Blob или null если нет в кэше.
 */
export async function getCachedPhoto(url: string): Promise<Blob | null> {
  const u = String(url || '').trim();
  if (isSkippableSrc(u)) return null;
  
  try {
    const db = await openDb();
    if (!db) return null;
    
    const tx = db.transaction(STORE_PHOTOS, 'readonly');
    const store = tx.objectStore(STORE_PHOTOS);
    const entry = await asPromise(store.get(u)) as PhotoCacheEntry | undefined;
    
    if (entry?.blob) return entry.blob;
    return null;
  } catch {
    return null;
  }
}

/**
 * Сохранить фото в постоянный кэш.
 * chatId нужен для последующей очистки при выходе из чата.
 */
export async function putPhotoToCache(url: string, blob: Blob, chatId: string, mime?: string): Promise<void> {
  const u = String(url || '').trim();
  if (isSkippableSrc(u) || !blob) return;
  
  try {
    const db = await openDb();
    if (!db) return;
    
    const tx = db.transaction(STORE_PHOTOS, 'readwrite');
    const store = tx.objectStore(STORE_PHOTOS);
    
    const existing = await asPromise(store.get(u)) as PhotoCacheEntry | undefined;
    if (existing) return; // уже есть
    
    const entry: PhotoCacheEntry = {
      url: u,
      blob,
      mime: String(mime || blob.type || '').trim(),
      chatId: String(chatId || '').trim(),
      ts: Date.now(),
    };
    
    await asPromise(store.put(entry));
  } catch {
    // ignore
  }
}

/**
 * Очистить кэш фотографий для конкретного чата.
 * Вызывается при выходе из чата.
 */
export async function clearPhotoCacheByChat(chatId: string): Promise<void> {
  const cid = String(chatId || '').trim();
  if (!cid) return;
  
  try {
    const db = await openDb();
    if (!db) return;
    
    const tx = db.transaction(STORE_PHOTOS, 'readwrite');
    const store = tx.objectStore(STORE_PHOTOS);
    const index = store.index('chatId');
    
    await new Promise<void>((resolve) => {
      const cur = index.openCursor(IDBKeyRange.only(cid));
      cur.onsuccess = () => {
        const c = cur.result;
        if (!c) return resolve();
        c.delete();
        c.continue();
      };
      cur.onerror = () => resolve();
    });
  } catch {
    // ignore
  }
}

/**
 * Полная очистка кэша фотографий.
 * Вызывается через настройки мессенджера.
 */
export async function clearAllPhotoCache(): Promise<void> {
  try {
    const db = await openDb();
    if (!db) return;
    
    const tx = db.transaction(STORE_PHOTOS, 'readwrite');
    const store = tx.objectStore(STORE_PHOTOS);
    await asPromise(store.clear());
  } catch {
    // ignore
  }
}

/**
 * Удалить базу данных полностью.
 */
export async function deletePhotoDb(): Promise<void> {
  try {
    if (!canUseIDB()) return;
    _db = null;
    indexedDB.deleteDatabase(DB_NAME);
  } catch {
    // ignore
  }
}

/**
 * Получить размер кэша (приблизительно).
 */
export async function getPhotoCacheSize(): Promise<number> {
  try {
    const db = await openDb();
    if (!db) return 0;
    
    const tx = db.transaction(STORE_PHOTOS, 'readonly');
    const store = tx.objectStore(STORE_PHOTOS);
    
    let total = 0;
    await new Promise<void>((resolve) => {
      const cur = store.openCursor();
      cur.onsuccess = () => {
        const c = cur.result;
        if (!c) return resolve();
        const entry = c.value as PhotoCacheEntry;
        if (entry?.blob) total += entry.blob.size;
        c.continue();
      };
      cur.onerror = () => resolve();
    });
    
    return total;
  } catch {
    return 0;
  }
}
